/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
"use client"

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/Components/ui/card"
import { StatusReportContext } from "./StatusReportContext"
import TrafficLightComponent from "../TrafficLightComponent"
import { formatDate } from "date-fns"
import { dateformat } from "../../utils/GlobalConstants"
import { useNavigate } from "react-router-dom"
import { getImpactRating } from "@/lib/utils"
//import { Checkbox } from "../ui/checkbox"
import React from "react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu"
import { CalendarRangeIcon, DeliverableIcon } from "../ui/Icons/TooltipIcon"
import { WizardContext } from "../wizard/wizardProvider"
import { useIsMobile } from "@/hooks/use-mobile"
import { useGlassmorphism } from "@/hooks/use-glassmorphism"

export type StatusReportChartRecord = {
    timeFrame: string
    planned: number
    actual?: number
}

export type StatusReportChartData = {
    data: StatusReportChartRecord[],
    timeFrame: 'Week' | 'Month' | 'Quarter' | 'Year',
    deliverablesOnly: boolean
}

export type WorkstreamHealth = {
    workstreamName?: string,
    workstreamId: number,
    lifecycleStage?: 1 | 2 | 3 | 4, // 1 = Initiation, 2 = Planning, 3 = Execution, 4 = Closure
    overall: 2 | 1 | 4,
    resources: 2 | 1 | 4,
    scope: 2 | 1 | 4,
    schedule: 2 | 1 | 4,
    financial: 2 | 1 | 4,
    other: 2 | 1 | 4
}

interface StatusReportChartProps {
    estimatedCloseDate: Date,
    health: WorkstreamHealth,
}

function StatusReportChart({ estimatedCloseDate, health }: StatusReportChartProps) {
    const status_report = React.useContext(StatusReportContext);
    const wizard = React.useContext(WizardContext);
    const isMobile = useIsMobile();
    const glassmorphismEnabled = useGlassmorphism();

    const [openMenu, setOpenMenu] = React.useState(false);
    const navigate = useNavigate();
    const [datasource, setDatasource] = React.useState<StatusReportChartRecord[]>([]);

    const [dataByWeek, setDataByWeek] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByWeekDO, setDataByWeekDO] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByMonth, setDataByMonth] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByMonthDO, setDataByMonthDO] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByQuarter, setDataByQuarter] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByQuarterDO, setDataByQuarterDO] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByYear, setDataByYear] = React.useState<StatusReportChartRecord[]>([]);
    const [dataByYearDO, setDataByYearDO] = React.useState<StatusReportChartRecord[]>([]);

    const [isDOToggleChecked, setIsDOToggleChecked] = React.useState(false);
    const [selectedTimeFrame, setSelectedTimeFrame] = React.useState<'Week' | 'Month' | 'Quarter' | 'Year'>('Week');

    const [displayRangeStart, setDisplayRangeStart] = React.useState<number>(0);
    const loadingMessage = "Loading chart data...";
    const noDataMessage = "No Data";
    const [chartMessage, setChartMessage] = React.useState<string>(loadingMessage);
    const [showChartMessage, setShowChartMessage] = React.useState<boolean>(false);

    React.useEffect(() => {
        setChartMessage(loadingMessage);
        setShowChartMessage(true);
       // loadChartData(false, null);
    }, []);

    React.useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick) {
            if ( wizard.currentStep.stepComponentId === "select-time-frame-button") {
                const timeoutId = setTimeout(() => {
                    setOpenMenu(true);
                    setTimeout(() => setSelectedTimeFrame('Week'), 1500);
                }, 1000);
                return () => clearTimeout(timeoutId);
            }
            if (wizard.currentStep.stepComponentId === "deliverables-only-button") {
                const timeoutId = setTimeout(() => {
                    setIsDOToggleChecked((prev) => !prev);
                }, 1000);
                return () => clearTimeout(timeoutId);
            }
            if (wizard.currentStep.stepComponentId === "display-range-slider") {
                const timeoutId = setTimeout(() => {
                    setDisplayRangeStart(14);
                }, 1000);
                return () => clearTimeout(timeoutId);
            }
        }

    }, [wizard.showWizardPointer, wizard.currentStep]);

    React.useEffect(() => {
        setDataByWeek([]);
        setDataByWeekDO([]);
        setDataByMonth([]);
        setDataByMonthDO([]);
        setDataByQuarter([]);
        setDataByQuarterDO([]);
        setDataByYear([]);
        setDataByYearDO([]);
        setDisplayRangeStart(0);
        loadChartData(isDOToggleChecked, null);
    }, [status_report.demoMode]);

    React.useEffect(() => {
        // Do not auto-fetch here. Only update datasource from already-loaded data.
        // Call loadChartData(...) manually where you want to control fetching.
        switch (selectedTimeFrame) {
            case 'Week':
                if (isDOToggleChecked) {
                    if (dataByWeekDO.length > 0) {
                        const slice = dataByWeekDO.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                } else {
                    if (dataByWeek.length > 0) {
                        const slice = dataByWeek.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                }
                break;
            case 'Month':
                if (isDOToggleChecked) {
                    if (dataByMonthDO.length > 0) {
                        const slice = dataByMonthDO.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                } else {
                    if (dataByMonth.length > 0) {
                        const slice = dataByMonth.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                }
                break;
            case 'Quarter':
                if (isDOToggleChecked) {
                    if (dataByQuarterDO.length > 0) {
                        const slice = dataByQuarterDO.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                } else {
                    if (dataByQuarter.length > 0) {
                        const slice = dataByQuarter.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                }
                break;
            case 'Year':
                if (isDOToggleChecked) {
                    if (dataByYearDO.length > 0) {
                        const slice = dataByYearDO.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                } else {
                    if (dataByYear.length > 0) {
                        const slice = dataByYear.slice(displayRangeStart, displayRangeStart + 14);
                        setDatasource(slice);
                        setShowChartMessage(false);
                    } else {
                        showNoDataMessageWithDelay();
                    }
                }
                break;
        }
    }, [dataByWeek, dataByWeekDO, dataByMonth, dataByMonthDO, dataByQuarter, dataByQuarterDO, dataByYear, dataByYearDO, isDOToggleChecked, selectedTimeFrame, displayRangeStart]);

    // Keep refs of latest state so the timeout callback can read current values (avoids stale closures)
    const dataByWeekRef = React.useRef(dataByWeek);
    const dataByWeekDORef = React.useRef(dataByWeekDO);
    const dataByMonthRef = React.useRef(dataByMonth);
    const dataByMonthDORef = React.useRef(dataByMonthDO);
    const dataByQuarterRef = React.useRef(dataByQuarter);
    const dataByQuarterDORef = React.useRef(dataByQuarterDO);
    const dataByYearRef = React.useRef(dataByYear);
    const dataByYearDORef = React.useRef(dataByYearDO);
    const isDOToggleCheckedRef = React.useRef(isDOToggleChecked);
    const selectedTimeFrameRef = React.useRef(selectedTimeFrame);

    React.useEffect(() => { dataByWeekRef.current = dataByWeek; }, [dataByWeek]);
    React.useEffect(() => { dataByWeekDORef.current = dataByWeekDO; }, [dataByWeekDO]);
    React.useEffect(() => { dataByMonthRef.current = dataByMonth; }, [dataByMonth]);
    React.useEffect(() => { dataByMonthDORef.current = dataByMonthDO; }, [dataByMonthDO]);
    React.useEffect(() => { dataByQuarterRef.current = dataByQuarter; }, [dataByQuarter]);
    React.useEffect(() => { dataByQuarterDORef.current = dataByQuarterDO; }, [dataByQuarterDO]);
    React.useEffect(() => { dataByYearRef.current = dataByYear; }, [dataByYear]);
    React.useEffect(() => { dataByYearDORef.current = dataByYearDO; }, [dataByYearDO]);
    React.useEffect(() => { isDOToggleCheckedRef.current = isDOToggleChecked; }, [isDOToggleChecked]);
    React.useEffect(() => { selectedTimeFrameRef.current = selectedTimeFrame; }, [selectedTimeFrame]);

    function showNoDataMessageWithDelay() {
        setTimeout(() => {
            // pick the current dataset based on latest refs
            const tf = selectedTimeFrameRef.current;
            const isDO = isDOToggleCheckedRef.current;
            const currentData =
                tf === 'Week' ? (isDO ? dataByWeekDORef.current : dataByWeekRef.current) :
                    tf === 'Month' ? (isDO ? dataByMonthDORef.current : dataByMonthRef.current) :
                        tf === 'Quarter' ? (isDO ? dataByQuarterDORef.current : dataByQuarterRef.current) :
                            (isDO ? dataByYearDORef.current : dataByYearRef.current);

            if (!currentData || currentData.length === 0) {
                setDatasource([]);
                setChartMessage(noDataMessage);
                setShowChartMessage(true);
            }
        }, 500);
    }

    function loadChartData(deliverablesOnly: boolean, timeFrame: 'Week' | 'Month' | 'Quarter' | 'Year' | null) {
        if (status_report.demoMode) {
            const generateDemoData = (timeFrame: 'Week' | 'Month' | 'Quarter' | 'Year'): StatusReportChartRecord[] => {
                const count = timeFrame === 'Week' ? 104 : timeFrame === 'Month' ? 24 : timeFrame === 'Quarter' ? 8 : 2;
                const data: StatusReportChartRecord[] = [];

                for (let i = 0; i < count; i++) {
                    const planned = Math.floor((i + 1) * (100 / count));
                    const actual = i < 7
                        ? Math.floor((i + 1) * (100 / count) * (0.85 + Math.random() * 0.1))
                        : undefined;
                    let label = '';
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    
                    switch (timeFrame) {
                        case 'Week':
                            label = `Week ${i + 1}`;
                            break;
                        case 'Month':
                            label = monthNames[i % 12];
                            break;
                        case 'Quarter':
                            label = `Q${(i % 4) + 1} ${Math.floor(i / 4) + 2024}`;
                            break;
                        case 'Year':
                            label = `${2024 + i}`;
                            break;
                    }

                    data.push({ timeFrame: label, planned, actual });
                }

                return data;
            };

            if (timeFrame === null) {
                setDataByWeek(generateDemoData('Week'));
                setDataByWeekDO(generateDemoData('Week').map(d => ({ ...d, actual: d.actual !== undefined ? d.actual - 2 : undefined })));
                setDataByMonth(generateDemoData('Month'));
                setDataByMonthDO(generateDemoData('Month').map(d => ({ ...d, actual: d.actual !== undefined ? d.actual - 2 : undefined })));
                setDataByQuarter(generateDemoData('Quarter'));
                setDataByQuarterDO(generateDemoData('Quarter').map(d => ({ ...d, actual: d.actual !== undefined ? d.actual - 1 : undefined })));
                setDataByYear(generateDemoData('Year'));
                setDataByYearDO(generateDemoData('Year').map(d => ({ ...d, actual: d.actual !== undefined ? d.actual - 1 : undefined })));
                setSelectedTimeFrame('Year');
            } else {
                const demoData = generateDemoData(timeFrame);
                switch (timeFrame) {
                    case 'Week':
                        deliverablesOnly ? setDataByWeekDO(demoData) : setDataByWeek(demoData);
                        break;
                    case 'Month':
                        deliverablesOnly ? setDataByMonthDO(demoData) : setDataByMonth(demoData);
                        break;
                    case 'Quarter':
                        deliverablesOnly ? setDataByQuarterDO(demoData) : setDataByQuarter(demoData);
                        break;
                    case 'Year':
                        deliverablesOnly ? setDataByYearDO(demoData) : setDataByYear(demoData);
                        break;
                }
            }
            return;
        }
        fetch(`Reports/StatusReportChartData/${health.workstreamId}?deliverablesOnly=${deliverablesOnly}&timeFrame=${timeFrame ? timeFrame : 'null'}`)
            .then(response => response.json())
            .then((chartData: StatusReportChartData) => {
                switch (chartData.timeFrame) {
                    case 'Week':
                        if (deliverablesOnly) {
                            setDataByWeekDO(chartData.data);
                        } else {
                            setDataByWeek(chartData.data);
                        }
                        break;
                    case 'Month':
                        if (deliverablesOnly) {
                            setDataByMonthDO(chartData.data);
                        } else {
                            setDataByMonth(chartData.data);
                        }
                        break;
                    case 'Quarter':
                        if (deliverablesOnly) {
                            setDataByQuarterDO(chartData.data);
                        } else {
                            setDataByQuarter(chartData.data);
                        }
                        break;
                    case 'Year':
                        if (deliverablesOnly) {
                            setDataByYearDO(chartData.data);
                        } else {
                            setDataByYear(chartData.data);
                        }
                        break;
                }

                if (timeFrame === null || chartData.timeFrame !== timeFrame) {
                    setSelectedTimeFrame(chartData.timeFrame);
                }
            })
            .catch(() => {
                setChartMessage("Failed to load chart data.");
                setShowChartMessage(true);
            })
    }

    const toolTipWrapperStyle = {
        backgroundColor: 'black',
        borderRadius: '5px',
        color: 'white',
        padding: '10px'
    };

    const toolTipContentStyle = {
        backgroundColor: 'black',
        border: 'none',
        color: 'white',
        padding: '10px',
    };

    // const CustomizedLabel = ({ x, y, stroke, value }: LabelProps) => {
    //     return (
    //         <text x={x} y={y} dy={-4} fill={stroke} fontSize={10} textAnchor="middle">
    //             {value}
    //         </text>
    //     );
    // };

    const CustomizedAxisTick = ({ x, y, payload }: any) => {
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={10} textAnchor="end" fill="#666" transform="rotate(-35)">
                    {payload.value}
                </text>
            </g>
        );
    };

    return (
        <Card id="status-report-chart" className={`flex flex-col ${glassmorphismEnabled ? 'glassmorphism-card' : ''} justify-center pb-2 ${isMobile ? "w-full h-full" : "w-1/2"}  relative`}>
            <table className="m-2 text-center">
                <thead className="bg-table-header text-table-header-foreground rounded-tr-md rounded-tl-md">
                    <tr className="">
                        <th className="px-4 py-2 rounded-tl-md">
                            <div className="flex flex-row">
                                <button id="deliverables-only-button" onClick={() => {
                                    setIsDOToggleChecked(!isDOToggleChecked);
                                    setDisplayRangeStart(0);
                                    if (!isDOToggleChecked) {
                                        // Now toggling to Deliverables Only
                                        if (selectedTimeFrame === 'Week' && dataByWeekDO.length === 0 ||
                                            selectedTimeFrame === 'Month' && dataByMonthDO.length === 0 ||
                                            selectedTimeFrame === 'Quarter' && dataByQuarterDO.length === 0 ||
                                            selectedTimeFrame === 'Year' && dataByYearDO.length === 0) {
                                            loadChartData(true, selectedTimeFrame);
                                        }
                                    } else {
                                        // Now toggling to All Items
                                        if (selectedTimeFrame === 'Week' && dataByWeek.length === 0 ||
                                            selectedTimeFrame === 'Month' && dataByMonth.length === 0 ||
                                            selectedTimeFrame === 'Quarter' && dataByQuarter.length === 0 ||
                                            selectedTimeFrame === 'Year' && dataByYear.length === 0) {
                                            loadChartData(false, selectedTimeFrame);
                                        }
                                    }
                                }}><DeliverableIcon tooltip="Display Only Deliverables" className={(isDOToggleChecked ? "text-white" : "text-gray-500")} /></button>
                                <DropdownMenu open={openMenu} onOpenChange={setOpenMenu} modal={false}>
                                    <DropdownMenuTrigger asChild>
                                        <button id="select-time-frame-button" className="ml-2 underline"><CalendarRangeIcon /></button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        <div className="flex flex-col space-y-2 p-2">
                                            <button className={selectedTimeFrame === 'Week' ? "bg-accent text-white" : ""} onClick={() => {
                                                setDisplayRangeStart(0);
                                                setSelectedTimeFrame('Week');
                                                if (!isDOToggleChecked && dataByWeek.length === 0 || isDOToggleChecked && dataByWeekDO.length === 0) {
                                                    loadChartData(isDOToggleChecked, 'Week');
                                                }
                                            }}>Week</button>
                                            <button className={selectedTimeFrame === 'Month' ? "bg-accent text-white" : ""} onClick={() => {
                                                setDisplayRangeStart(0);
                                                setSelectedTimeFrame('Month');
                                                if (!isDOToggleChecked && dataByMonth.length === 0 || isDOToggleChecked && dataByMonthDO.length === 0) {
                                                    loadChartData(isDOToggleChecked, 'Month');
                                                }
                                            }}>Month</button>
                                            <button className={selectedTimeFrame === 'Quarter' ? "bg-accent text-white" : ""} onClick={() => {
                                                setDisplayRangeStart(0);
                                                setSelectedTimeFrame('Quarter');
                                                if (!isDOToggleChecked && dataByQuarter.length === 0 || isDOToggleChecked && dataByQuarterDO.length === 0) {
                                                    loadChartData(isDOToggleChecked, 'Quarter');
                                                }
                                            }}>Quarter</button>
                                            <button className={selectedTimeFrame === 'Year' ? "bg-accent text-white" : ""} onClick={() => {
                                                setDisplayRangeStart(0);
                                                setSelectedTimeFrame('Year');
                                                if (!isDOToggleChecked && dataByYear.length === 0 || isDOToggleChecked && dataByYearDO.length === 0) {
                                                    loadChartData(isDOToggleChecked, 'Year');
                                                }
                                            }}>Year</button>
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </th>
                        <th className="px-4 py-2 text-nowrap">Estimated Close Date</th>
                        <th className="px-4 py-2">Scope</th>
                        <th className="px-4 py-2">Schedule</th>
                        <th className="px-4 py-2">Resources</th>
                        <th className="px-4 py-2">Financial</th>
                        <th className="px-4 py-2 rounded-tr-md">Other</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="px-2 py-auto font-bold">

                        </td>
                        <td id="estimated-close-date" className="px-2 4 py-2">{formatDate(new Date(estimatedCloseDate), dateformat)}</td>
                        <td className="px-2 4 py-2" onClick={() => {
                            health.scope === 4 ? navigate('/Charter') : navigate('/Charter', {
                                state: [
                                    { id: "workstream", value: health.workstreamName },
                                    { id: "status", value: "Open" },
                                    { id: "impactRating", value: getImpactRating(health.scope) }]
                            })
                        }}><TrafficLightComponent id="scope-traffic-light" TrafficLightColour={health.scope} Size={5} /></td>
                        <td className="px-2 4 py-2" onClick={() => navigate('/Plan', {
                            state: { filterCombo: health.schedule === 4 ? 0 : health.schedule * 128, workstreamId: health.workstreamId }
                        })}><TrafficLightComponent id="schedule-traffic-light" TrafficLightColour={health.schedule} Size={5} /></td>
                        <td className="px-2 4 py-2" onClick={() => navigate('/SupportRequests', {
                            state: [
                                { id: "workstream", value: health.workstreamName },
                                { id: "status", value: "Open" },
                                { id: "impactRating", value: getImpactRating(health.resources) },
                                { id: "requestType", value: "Resource" }
                            ]
                        })}><TrafficLightComponent id="resources-traffic-light" TrafficLightColour={health.resources} Size={5} /></td>
                        <td className="px-2 4 py-2" onClick={() => navigate('/SupportRequests', {
                            state: [
                                { id: "workstream", value: health.workstreamName },
                                { id: "requestType", value: "Financial" },
                                { id: "status", value: "Open" },
                                { id: "impactRating", value: getImpactRating(health.financial) }
                            ]
                        })}><TrafficLightComponent id="financial-traffic-light" TrafficLightColour={health.financial} Size={5} /></td>
                        <td className="px-2 4 py-2" onClick={() => navigate('/SupportRequests', {
                            state: [
                                { id: "workstream", value: health.workstreamName },
                                { id: "requestType", value: "Other" },
                                { id: "status", value: "Open" },
                                { id: "impactRating", value: getImpactRating(health.other) }
                            ]
                        })}><TrafficLightComponent id="other-traffic-light" TrafficLightColour={health.other} Size={5} /></td>
                    </tr>
                </tbody>
            </table>
            <ResponsiveContainer id="progress-chart" width="95%" height={isMobile ? 300 : "80%"}>
                <LineChart key={`${selectedTimeFrame}-${datasource.length}`} width={730} height={250} data={datasource}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                    <XAxis dataKey="timeFrame" height={selectedTimeFrame === 'Week' ? 60 : 50} tick={<CustomizedAxisTick />} minTickGap={-50} />
                    <YAxis />
                    <Tooltip wrapperStyle={toolTipWrapperStyle} contentStyle={toolTipContentStyle} />
                    <Legend />
                    <Line type="linear" dataKey="planned" stroke="#2463eb" />
                    <Line type="linear" dataKey="actual" stroke="#61A6FA" />
                </LineChart>
            </ResponsiveContainer>
            {(() => {
                const currentData = selectedTimeFrame === 'Week'
                    ? (isDOToggleChecked ? dataByWeekDO : dataByWeek)
                    : selectedTimeFrame === 'Month'
                        ? (isDOToggleChecked ? dataByMonthDO : dataByMonth)
                        : selectedTimeFrame === 'Quarter'
                            ? (isDOToggleChecked ? dataByQuarterDO : dataByQuarter)
                            : (isDOToggleChecked ? dataByYearDO : dataByYear);

                const maxStart = Math.max(0, currentData.length - 14);
                if (currentData.length <= 14) {
                    return null;
                }
                return (
                    <div className="flex items-center space-x-2 absolute bottom-4 left-3/4 transform -translate-x-2/3">
                        <input
                            id="display-range-slider"
                            type="range"
                            min={0}
                            max={maxStart}
                            value={displayRangeStart}
                            onChange={(e) => setDisplayRangeStart(Number(e.target.value))}
                            className="w-40 accent-blue-600"
                        />
                    </div>
                );
            })()}
            {showChartMessage && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white px-4 py-2 rounded">
                    {chartMessage}
                </div>
            )}
            {/* <div className="mx-auto text-sm">Deliverables Only <Checkbox onCheckedChange={(checked) => checked ? setDatasource(secondaryData) : setDatasource(data)} /></div> */}
        </Card>
    )
}

export default StatusReportChart